import store from "@extension-context-common/store/extension-store";
import { setUpdateAvailable } from "@common-reducers/UpdateNotificationReducer";

/**
 * Returns true when version `a` is newer than version `b`.
 * Compares dot-separated numeric parts ("5.8.10" > "5.8.9").
 */
export function isVersionNewer(a: string, b: string): boolean {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
}

/**
 * Singleton service for Chrome extension update notifications
 * Listens for UPDATE_AVAILABLE messages from background script
 * and dispatches Redux actions to update the UI
 */
class UpdateNotificationService {
  private static instance: UpdateNotificationService | null = null;
  private listenerRegistered = false;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): UpdateNotificationService {
    if (!UpdateNotificationService.instance) {
      UpdateNotificationService.instance = new UpdateNotificationService();
    }
    return UpdateNotificationService.instance;
  }

  /**
   * Initialize the update notification listener
   * Safe to call multiple times - will only register once
   */
  public initialize(): void {
    if (this.listenerRegistered) {
      console.log('[UpdateNotificationService] Already initialized, skipping');
      return;
    }

    console.log('[UpdateNotificationService] Initializing update notification listener');
    this.listenerRegistered = true;

    // Register Chrome message listener
    const updateListener = (
      message: any,
      sender: chrome.runtime.MessageSender,
      sendResponse: (response?: any) => void
    ) => {
      if (message.type === 'UPDATE_AVAILABLE') {
        console.log('[UpdateNotificationService] Update available message received:', message.payload);

        // Dispatch Redux action to show update notification
        store.dispatch(
          setUpdateAvailable({
            currentVersion: message.payload.currentVersion,
            newVersion: message.payload.newVersion,
          })
        );

        sendResponse({ received: true });
      }
    };

    chrome.runtime?.onMessage?.addListener(updateListener);

    // Check for existing update state on initialization
    chrome.storage?.local.get('updateState', (result) => {
      if (result.updateState && result.updateState.updateAvailable) {
        // Stale-state guard: if the running extension is already at (or past)
        // the "new" version, the update was installed but the state was never
        // cleared. Showing it would offer an update that can't succeed —
        // chrome.runtime.reload() with no pending update never fires
        // onInstalled('update'), leaving the modal stuck on "Updating...".
        const installedVersion = chrome.runtime?.getManifest?.().version;
        if (
          installedVersion &&
          result.updateState.newVersion &&
          !isVersionNewer(result.updateState.newVersion, installedVersion)
        ) {
          console.log('[UpdateNotificationService] Clearing stale update state (already on', installedVersion + ')');
          chrome.storage.local.remove('updateState');
          return;
        }
        console.log('[UpdateNotificationService] Found existing update state:', result.updateState);
        store.dispatch(
          setUpdateAvailable({
            currentVersion: result.updateState.currentVersion,
            newVersion: result.updateState.newVersion,
          })
        );
      }
    });

    console.log('[UpdateNotificationService] Update notification listener registered');
  }

  /**
   * Check if the service is initialized
   */
  public isInitialized(): boolean {
    return this.listenerRegistered;
  }
}

// Export singleton instance
export default UpdateNotificationService.getInstance();
